import type { PartialMessage } from '@bufbuild/protobuf';

import type {
  CreateLiveTranscriptionSessionResp,
  DocumentAnswerChange as DocumentAnswerChangeMessage,
  ExportPartnerDocumentResp,
} from '@/shared/api/protocol-ts/api/scribe/svc_encounter_pb';
import {
  DocumentAnswerSource as DocumentAnswerSourceMessage,
  NoteCompletionStatus as NoteCompletionStatusMessage,
  type DocumentAnswer as DocumentAnswerMessage,
  type DocumentAnswers as DocumentAnswersMessage,
  type NoteCompletion as NoteCompletionMessage,
  type Transcript as TranscriptMessage,
} from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';

import type {
  DocumentAnswer,
  DocumentAnswerChange,
  DocumentAnswers,
  DocumentAnswerSource,
  LiveTranscriptionSession,
  NoteCompletion,
  NoteCompletionStatus,
  PartnerDocumentExport,
  TranscriptChunk,
} from './transcriptorTypes';

// An unknown source cannot claim manual ownership, so it is treated as AI.
const sourceToDomain = (
  source: DocumentAnswerSourceMessage,
): DocumentAnswerSource =>
  source === DocumentAnswerSourceMessage.DocumentAnswerSourceManual
    ? 'manual'
    : 'ai';

const answerToDomain = (answer: DocumentAnswerMessage): DocumentAnswer => ({
  key: {
    sectionCode: answer.Key?.SectionCode ?? '',
    optionIndex: answer.Key?.OptionIndex ?? -1,
    slot: answer.Key?.Slot ?? '',
  },
  values: answer.Values,
  text: answer.Text,
  source: sourceToDomain(answer.Source),
  cleared: answer.Cleared,
  optionText: answer.OptionText,
  evidenceChunkIndexes: answer.EvidenceChunkIndexes,
});

export const documentAnswersToDomain = (
  answers: DocumentAnswersMessage | undefined,
): DocumentAnswers | null =>
  answers
    ? {
        encounterId: answers.EncounterID,
        documentId: answers.DocumentID,
        templateId: answers.TemplateID,
        revision: Number(answers.Revision),
        transcriptRevision: Number(answers.TranscriptRevision),
        appliedTranscriptRevision: Number(answers.AppliedTranscriptRevision),
        answers: answers.Answers.map(answerToDomain),
        notes: answers.Notes.map((note) => ({
          sectionCode: note.SectionCode,
          text: note.Text,
          source: sourceToDomain(note.Source),
        })),
        ...(answers.View && { note: answers }),
      }
    : null;

const COMPLETION_STATUSES: Partial<
  Record<NoteCompletionStatusMessage, NoteCompletionStatus>
> = {
  [NoteCompletionStatusMessage.NoteCompletionStatusQueued]: 'queued',
  [NoteCompletionStatusMessage.NoteCompletionStatusRunning]: 'running',
  [NoteCompletionStatusMessage.NoteCompletionStatusFinished]: 'finished',
  [NoteCompletionStatusMessage.NoteCompletionStatusFailed]: 'failed',
};

// A status this client does not know is no request it could follow.
export const noteCompletionToDomain = (
  completion: NoteCompletionMessage | undefined,
): NoteCompletion | null => {
  const status = completion && COMPLETION_STATUSES[completion.Status];
  return completion && status
    ? {
        id: completion.ID,
        status,
        instructions: completion.Instructions,
        saidCount: completion.SaidCount,
        assumedCount: completion.AssumedCount,
        suggestedCount: completion.SuggestedCount,
        unresolvedLineCount: completion.UnresolvedLineCount,
      }
    : null;
};

export const documentAnswerChangeToWire = (
  change: DocumentAnswerChange,
): PartialMessage<DocumentAnswerChangeMessage> => ({
  Key: {
    SectionCode: change.key.sectionCode,
    OptionIndex: change.key.optionIndex,
    Slot: change.key.slot,
  },
  Values: change.values ?? [],
  Text: change.text ?? '',
  Clear: !!change.clear,
  Release: !!change.release,
});

export const liveTranscriptToWire = ({
  language,
  chunks,
}: {
  language: string;
  chunks: TranscriptChunk[];
}): PartialMessage<TranscriptMessage> => ({
  Lang: language,
  Chunks: chunks.map((chunk) => ({
    Text: chunk.text,
    Start: chunk.start,
    End: chunk.end,
  })),
});

export const liveSessionToDomain = (
  response: CreateLiveTranscriptionSessionResp,
): LiveTranscriptionSession => ({
  provider: response.Provider,
  model: response.Model,
  clientSecret: response.ClientSecret,
  expiresAt: response.ExpiresAt?.toDate().toISOString() ?? null,
  connectUrl: response.ConnectURL,
  language: response.Language,
});

export const partnerExportToDomain = (
  response: ExportPartnerDocumentResp,
): PartnerDocumentExport => ({
  partner: response.Partner,
  format: response.Format,
  content: response.Content,
  plainText: response.PlainText,
  warnings: response.Warnings.map((warning) => ({
    kind: warning.Kind,
    lineId: warning.LineID,
    blockId: warning.BlockID,
    message: warning.Message,
  })),
  revision: Number(response.Revision),
});
